import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip"; // Added Tooltip imports
import { useState, useEffect } from "react";
import API from "../api/api01";
import { toast } from "sonner";
import {
  XCircle,
  Search,
  Filter,
  TrendingDown,
  AlertTriangle,
  RotateCcw,
  Trash2, // Added Trash icon
  DollarSign,
} from "lucide-react";

interface LostLead {
  id: string;
  name: string;
  email: string;
  phone: string;
  course: string;
  lostReason: "Price" | "Competitor" | "Not Interested" | "Course Not Available" | "Location" | "Other";
  lostDate: string;
  potentialRevenue: number;
  daysInPipeline: number;
  lastStage: string;
  counselor: {
    name: string;
    avatar: string;
  };
  notes: string;
}

export function LeadLost() {
  const [searchQuery, setSearchQuery] = useState("");
  const [reasonFilter, setReasonFilter] = useState("all");

  const [lostLeads, setLostLeads] = useState<LostLead[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchLostLeads = async () => {
    setLoading(true);
    try {
      const response = await API.get("/leads/");
      if (response.data) {
        const allLost = response.data.flatMap((s: any) => 
          s.leads.filter((l: any) => l.lead_status === "not-interested" || l.lead_status === "Lost")
          .map((l: any) => ({
            id: l.id,
            name: l.name,
            email: l.email,
            phone: l.phone || "N/A",
            course: l.course || "N/A",
            lostReason: "Not Interested",
            lostDate: l.updated_at ? new Date(l.updated_at).toLocaleDateString() : "Recently",
            potentialRevenue: 0,
            daysInPipeline: 0,
            lastStage: s.name,
            counselor: {
              name: l.counselor_name || "Unassigned",
              avatar: l.counselor_avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${l.name}`,
            },
            notes: l.notes || "No notes provided.",
          }))
        );
        setLostLeads(allLost);
      }
    } catch (err) {
      console.error("Failed to fetch lost leads", err);
      toast.error("Failed to load lost leads");
    } finally {
      setLoading(false);
    }
  };

  const handleRestoreLead = async (id: string) => {
    try {
      await API.patch(`/leads/${id}`, { lead_status: "contacted" });
      toast.success("Lead restored to Contacted stage!");
      fetchLostLeads();
    } catch (err) {
      toast.error("Failed to restore lead");
    }
  };

  const handleDiscardLead = async (id: string) => {
    try {
      await API.delete(`/leads/${id}`);
      toast.success("Lead discarded successfully");
      fetchLostLeads();
    } catch (err) {
      toast.error("Failed to discard lead");
    }
  };

  useEffect(() => {
    fetchLostLeads();
  }, []);

  const getReasonColor = (reason: string) => {
    switch (reason) {
      case "Price": return "bg-red-500/10 text-red-500";
      case "Competitor": return "bg-orange-500/10 text-orange-500";
      case "Not Interested": return "bg-gray-500/10 text-gray-500";
      case "Course Not Available": return "bg-purple-500/10 text-purple-500";
      case "Location": return "bg-blue-500/10 text-blue-500";
      case "Other": return "bg-yellow-500/10 text-yellow-500";
      default: return "";
    }
  };

  const calculateStats = () => {
    const totalLost = lostLeads.length;
    const lostRevenue = lostLeads.reduce((sum, lead) => sum + lead.potentialRevenue, 0);
    const priceRelated = lostLeads.filter(lead => lead.lostReason === "Price").length;
    const competitorRelated = lostLeads.filter(lead => lead.lostReason === "Competitor").length;
    return { totalLost, lostRevenue, priceRelated, competitorRelated };
  };

  const stats = calculateStats();

  const filteredLeads = lostLeads.filter((lead) => {
    const matchesSearch =
      lead.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.course.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = reasonFilter === "all" || lead.lostReason === reasonFilter;
    return matchesSearch && matchesFilter;
  });

  return (
    <TooltipProvider> {/* Required for Tooltips to work */}
      <div className="p-4 md:p-6 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold">Lost Leads</h1>
          <p className="text-muted-foreground mt-1">
            Analyze lost opportunities and identify improvement areas
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-red-500/10">
                  <XCircle className="h-6 w-6 text-red-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.totalLost}</p>
                  <p className="text-sm text-muted-foreground">Total Lost</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-orange-500/10">
                  <DollarSign className="h-6 w-6 text-orange-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">₹{(stats.lostRevenue / 1000).toFixed(0)}k</p>
                  <p className="text-sm text-muted-foreground">Lost Revenue</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-yellow-500/10">
                  <TrendingDown className="h-6 w-6 text-yellow-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.priceRelated}</p>
                  <p className="text-sm text-muted-foreground">Price Related</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-lg bg-purple-500/10">
                  <AlertTriangle className="h-6 w-6 text-purple-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.competitorRelated}</p>
                  <p className="text-sm text-muted-foreground">To Competitors</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="shadow-sm">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search lost leads..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-full rounded-lg"
                />
              </div>

              <Select value={reasonFilter} onValueChange={setReasonFilter}>
                <SelectTrigger className="w-full md:w-48">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Lost Reason" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Reasons</SelectItem>
                  <SelectItem value="Price">Price</SelectItem>
                  <SelectItem value="Competitor">Competitor</SelectItem>
                  <SelectItem value="Not Interested">Not Interested</SelectItem>
                  <SelectItem value="Course Not Available">Course Not Available</SelectItem>
                  <SelectItem value="Location">Location</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Lost Leads Table */}
        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle>Lost Lead Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Lead Name</TableHead>
                    <TableHead>Course</TableHead>
                    <TableHead>Lost Reason</TableHead>
                    <TableHead className="text-right">Lost Revenue</TableHead>
                    <TableHead className="hidden md:table-cell">Days in Pipeline</TableHead>
                    <TableHead className="hidden md:table-cell">Lost Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredLeads.map((lead) => (
                    <TableRow key={lead.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={lead.counselor.avatar} />
                            <AvatarFallback>{lead.name.substring(0, 2)}</AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{lead.name}</p>
                            <p className="text-xs text-muted-foreground">{lead.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{lead.course}</TableCell>
                      <TableCell>
                        <Badge className={getReasonColor(lead.lostReason)}>
                          {lead.lostReason}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium text-red-600">
                        ₹{lead.potentialRevenue.toLocaleString()}
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-muted-foreground">
                        {lead.daysInPipeline} days
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-muted-foreground">
                        {lead.lostDate}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {/* Recover Button */}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="icon"
                                variant="outline"
                                className="h-8 w-8 rounded-full border-blue-200 text-blue-600 hover:bg-blue-50"
                                onClick={() => handleRestoreLead(lead.id)}
                              >
                                <RotateCcw className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Recover Lead</p>
                            </TooltipContent>
                          </Tooltip>
                          
                          {/* Discard Button */}
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="icon"
                                variant="outline"
                                className="h-8 w-8 rounded-full border-red-200 text-red-600 hover:bg-red-50"
                                onClick={() => handleDiscardLead(lead.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Discard Lead</p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </TooltipProvider>
  );
}